'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { countries } from '@/constants/countries';

const MOBILE_BREAKPOINT = 768;

export interface CountryCodeSelectProps {
  value: string;
  onValueChange: (dialCode: string) => void;
  placeholder?: string;
  triggerClassName?: string;
  /** Set to true when rendered inside a bottom sheet so dropdown opens upward and stays above sheet */
  inBottomSheet?: boolean;
}

export function CountryCodeSelect({
  value,
  onValueChange,
  placeholder = '+91',
  triggerClassName,
  inBottomSheet = false,
}: CountryCodeSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [openUp, setOpenUp] = useState(false);

  useEffect(() => {
    if (inBottomSheet) {
      setOpenUp(true);
      return;
    }
    const check = () => setOpenUp(window.innerWidth < MOBILE_BREAKPOINT);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [inBottomSheet]);

  const selected =
    countries.find(c => c.code === value) ??
    countries.find(c => c.code === '+91');

  const filteredCountries = useMemo(() => {
    if (!search.trim()) return countries;
    const q = search.trim().toLowerCase();
    return countries.filter(
      c =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        c.shortCode.toLowerCase().includes(q)
    );
  }, [search]);

  const handleSelect = (code: string) => {
    onValueChange(code);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          className={cn(
            'border-input flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-secondary bg-white px-3 text-sm shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:opacity-50',
            triggerClassName
          )}
          aria-haspopup='listbox'
          aria-expanded={open}
        >
          <span className='flex items-center gap-2 min-w-0'>
            {selected && (
              <ReactCountryFlag
                countryCode={selected.shortCode}
                svg
                style={{ width: '1.25em', height: '1.25em' }}
                className='shrink-0'
              />
            )}
            <span className='truncate'>
              {selected ? selected.code : placeholder}
            </span>
          </span>
          <ChevronDownIcon />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className='min-w-[280px] w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-md border bg-white p-0 z-[10001]'
        align='start'
        side={openUp ? 'top' : 'bottom'}
        sideOffset={6}
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <div className='p-1 border-b border-border'>
          <Input
            type='text'
            placeholder='Search country or code...'
            value={search}
            onChange={e => setSearch(e.target.value)}
            className='h-9 border-secondary text-sm'
          />
        </div>
        <div className='max-h-[280px] overflow-y-auto p-1'>
          {filteredCountries.length === 0 ? (
            <div className='py-6 text-center text-sm text-muted-foreground'>
              No country found.
            </div>
          ) : (
            filteredCountries.map(c => (
              <button
                key={`${c.name}-${c.code}`}
                type='button'
                className={cn(
                  'relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-none select-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
                  c.code === value && 'bg-accent text-accent-foreground'
                )}
                onClick={() => handleSelect(c.code)}
              >
                <span className='flex items-center gap-2 min-w-0'>
                  <ReactCountryFlag
                    countryCode={c.shortCode}
                    svg
                    style={{ width: '1.25em', height: '1.25em' }}
                    className='shrink-0'
                  />
                  <span className='truncate'>
                    {c.name} ({c.code})
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
